"use client"

import { useState, useEffect } from "react"
import "./MissionControl.css"

const MissionControl = ({ gameState, setGameState, addNotification }) => {
  const [activeMissions, setActiveMissions] = useState([])
  const [availableMissions, setAvailableMissions] = useState([])
  const [missionLog, setMissionLog] = useState([])
  const [nasaData, setNasaData] = useState({
    asteroids: [],
    solarFlares: [],
    weather: null,
    iss: null,
  })

  // Use a working NASA API key or fallback to simulated data
  const NASA_API_KEY = "DEMO_KEY"

  useEffect(() => {
    generateMissions()
    fetchNASAData()
    const interval = setInterval(fetchNASAData, 300000) // Update every 5 minutes
    return () => clearInterval(interval)
  }, [])

  const fetchNASAData = async () => {
    try {
      // Try to fetch real NASA data with CORS-friendly endpoints
      const today = new Date().toISOString().split("T")[0]

      // Use a CORS proxy for NASA APIs
      const proxyUrl = "https://api.allorigins.win/raw?url="

      try {
        // Fetch ISS Location (this usually works without CORS issues)
        const issResponse = await fetch("https://api.wheretheiss.at/v1/satellites/25544")
        const issData = await issResponse.json()

        setNasaData((prev) => ({
          ...prev,
          iss: {
            latitude: issData.latitude,
            longitude: issData.longitude,
            altitude: issData.altitude,
            velocity: issData.velocity,
          },
        }))
      } catch (issError) {
        console.log("ISS API failed, using simulated data")
      }

      // Generate simulated space events based on real patterns
      const simulatedAsteroids = [
        {
          name: `2024 ${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 999)}`,
          estimated_diameter: { kilometers: { estimated_diameter_max: Math.random() * 2 + 0.1 } },
          potentially_hazardous: Math.random() < 0.1,
        },
        {
          name: `2024 ${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 999)}`,
          estimated_diameter: { kilometers: { estimated_diameter_max: Math.random() * 1.5 + 0.2 } },
          potentially_hazardous: Math.random() < 0.15,
        },
      ]

      const simulatedFlares =
        Math.random() < 0.3
          ? [
              {
                beginTime: new Date().toISOString(),
                classType: `${["C", "M", "X"][Math.floor(Math.random() * 3)]}${(Math.random() * 9 + 1).toFixed(1)}`,
                peakTime: new Date(Date.now() + Math.random() * 3600000).toISOString(),
              },
            ]
          : []

      setNasaData((prev) => ({
        ...prev,
        asteroids: simulatedAsteroids,
        solarFlares: simulatedFlares,
        weather: {
          temperature: -60 + Math.random() * 40,
          wind_speed: Math.random() * 25,
          pressure: 600 + Math.random() * 300,
          dust_storm: Math.random() < 0.1,
        },
      }))

      // Generate space event notifications based on simulated data
      if (simulatedAsteroids.some((a) => a.potentially_hazardous)) {
        addNotification({
          type: "warning",
          title: "⚠️ Asteroid Threat Detected",
          description: "Potentially hazardous asteroid detected! Prepare defensive measures!",
        })
      }

      if (simulatedFlares.length > 0 && simulatedFlares[0].classType.startsWith("X")) {
        addNotification({
          type: "error",
          title: "☀️ EXTREME Solar Flare",
          description: "X-class solar flare detected! Radiation levels critical!",
        })

        // Apply immediate radiation damage
        setGameState((prev) => ({
          ...prev,
          colonyHealth: Math.max(0, prev.colonyHealth - 15),
          resources: {
            ...prev.resources,
            energy: Math.max(0, prev.resources.energy - 100), // Solar flare damages electronics
          },
        }))
      }
    } catch (error) {
      console.log("NASA API unavailable, using simulated data:", error)
      // Fallback to simulated data
      setNasaData({
        asteroids: [
          { name: "2024 Simulated-1", estimated_diameter: { kilometers: { estimated_diameter_max: 0.5 } } },
          { name: "2024 Simulated-2", estimated_diameter: { kilometers: { estimated_diameter_max: 1.2 } } },
        ],
        solarFlares: [{ beginTime: new Date().toISOString(), classType: "M1.5" }],
        iss: { latitude: "25.4", longitude: "-80.1", altitude: 408, velocity: 27600 },
        weather: { temperature: -65, wind_speed: 12, pressure: 750, dust_storm: false },
      })
    }
  }

  const generateMissions = () => {
    const missions = [
      {
        id: 1,
        title: "Emergency Resource Extraction",
        description: "Mine critical materials from nearby asteroid belt",
        type: "exploration",
        duration: "2m",
        requirements: { materials: 50, energy: 100 },
        rewards: { materials: 300, knowledge: 25 },
        risks: { population: -10, energy: -50 },
        difficulty: "Medium",
        status: "available",
        unlocked: true,
      },
      {
        id: 2,
        title: "Deep Space Communication",
        description: "Establish contact with Earth - may receive bad news",
        type: "research",
        duration: "1m",
        requirements: { energy: 75, knowledge: 25 },
        rewards: { knowledge: 150, colonyMorale: 20 },
        risks: { colonyMorale: -30, energy: -25 },
        difficulty: "Easy",
        status: "available",
        unlocked: true,
      },
      {
        id: 3,
        title: "Dangerous Rescue Operation",
        description: "Save colonists from failed expedition - high casualty risk",
        type: "rescue",
        duration: "3m",
        requirements: { energy: 150, materials: 75 },
        rewards: { population: 50, colonyMorale: 25 },
        risks: { population: -25, materials: -50 },
        difficulty: "Hard",
        status: "available",
        unlocked: true,
      },
      {
        id: 4,
        title: "Solar Storm Defense",
        description: "Deploy shields against incoming solar radiation",
        type: "defense",
        duration: "2m",
        requirements: { energy: 200, materials: 100 },
        rewards: { colonyHealth: 20, defenseRating: 15 },
        risks: { energy: -100, materials: -75 },
        difficulty: "Medium",
        status: "available",
        unlocked: true,
      },
      {
        id: 5,
        title: "Biological Sample Hunt",
        description: "Collect samples from hostile alien environment",
        type: "science",
        duration: "4m",
        requirements: { materials: 40, knowledge: 50 },
        rewards: { knowledge: 200, evolutionPoints: 50 },
        risks: { population: -15, colonyHealth: -10 },
        difficulty: "Hard",
        status: "available",
        unlocked: true,
      },
    ]

    setAvailableMissions(missions)
  }

  const startMission = (mission) => {
    const { requirements } = mission
    const { resources } = gameState

    // Check if player has required resources
    const canStart = Object.entries(requirements).every(([resource, amount]) => {
      return resources[resource] >= amount
    })

    if (!canStart) {
      addNotification({
        type: "error",
        title: "Insufficient Resources",
        description: "You don't have enough resources to start this mission.",
      })
      return
    }

    // Deduct resources
    setGameState((prev) => ({
      ...prev,
      resources: {
        ...prev.resources,
        ...Object.fromEntries(
          Object.entries(requirements).map(([resource, amount]) => [
            resource,
            Math.max(0, prev.resources[resource] - amount),
          ]),
        ),
      },
    }))

    // Start mission with risk calculation
    const durationMs = Number.parseInt(mission.duration) * 60 * 1000 // Convert minutes to milliseconds
    const activeMission = {
      ...mission,
      status: "active",
      startTime: Date.now(),
      endTime: Date.now() + durationMs,
      timeLeft: durationMs,
    }

    setActiveMissions((prev) => [...prev, activeMission])
    setAvailableMissions((prev) => prev.filter((m) => m.id !== mission.id))

    addNotification({
      type: "success",
      title: "Mission Launched",
      description: `${mission.title} is underway. ETA: ${mission.duration}`,
    })
  }

  const completeMission = (mission) => {
    const { rewards, risks } = mission

    // Calculate mission outcome (70% success rate)
    const success = Math.random() < 0.7

    if (success) {
      // Apply rewards
      setGameState((prev) => {
        const newState = { ...prev }

        Object.entries(rewards).forEach(([reward, amount]) => {
          if (reward === "evolutionPoints") {
            newState.user.evolutionPoints += amount
          } else if (reward === "colonyMorale") {
            newState.colonyMorale = Math.min(100, newState.colonyMorale + amount)
          } else if (reward === "colonyHealth") {
            newState.colonyHealth = Math.min(100, newState.colonyHealth + amount)
          } else if (reward === "population") {
            newState.population += amount
          } else if (reward === "defenseRating") {
            newState.defenseRating = (newState.defenseRating || 50) + amount
          } else if (newState.resources[reward] !== undefined) {
            newState.resources[reward] += amount
          }
        })

        return newState
      })

      addNotification({
        type: "success",
        title: "🎉 Mission Success",
        description: `${mission.title} completed successfully! Rewards received.`,
      })
    } else {
      // Apply risks/penalties
      setGameState((prev) => {
        const newState = { ...prev }

        Object.entries(risks || {}).forEach(([risk, amount]) => {
          if (risk === "colonyMorale") {
            newState.colonyMorale = Math.max(0, newState.colonyMorale + amount)
          } else if (risk === "colonyHealth") {
            newState.colonyHealth = Math.max(0, newState.colonyHealth + amount)
          } else if (risk === "population") {
            newState.population = Math.max(0, newState.population + amount)
          } else if (newState.resources[risk] !== undefined) {
            newState.resources[risk] = Math.max(0, newState.resources[risk] + amount)
          }
        })

        return newState
      })

      addNotification({
        type: "error",
        title: "💀 Mission Failed",
        description: `${mission.title} failed! Casualties and losses sustained.`,
      })
    }

    // Move to completed missions
    setActiveMissions((prev) => prev.filter((m) => m.id !== mission.id))
    setMissionLog((prev) => [
      { ...mission, status: success ? "completed" : "failed", completedAt: Date.now() },
      ...prev.slice(0, 9),
    ])

    // Return mission to available after delay
    setTimeout(() => {
      setAvailableMissions((prev) => [...prev, { ...mission, status: "available" }])
    }, 30000)
  }

  // Update active missions timer
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMissions((prev) =>
        prev
          .map((mission) => {
            const timeLeft = mission.endTime - Date.now()
            if (timeLeft <= 0) {
              completeMission(mission)
              return null
            }
            return { ...mission, timeLeft }
          })
          .filter(Boolean),
      )
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  const getMissionTypeIcon = (type) => {
    const icons = {
      exploration: "🚀",
      research: "🔬",
      rescue: "🆘",
      defense: "🛡️",
      science: "🧬",
    }
    return icons[type] || "🎯"
  }

  const getDifficultyColor = (difficulty) => {
    const colors = {
      Easy: "#4caf50",
      Medium: "#ff9800",
      Hard: "#f44336",
    }
    return colors[difficulty] || "#4caf50"
  }

  const canStartMission = (mission) => {
    return Object.entries(mission.requirements).every(([resource, amount]) => {
      return gameState.resources[resource] >= amount
    })
  }

  return (
    <div className="mission-control">
      <div className="mission-header">
        <div className="header-content">
          <h1 className="mission-title">
            <span className="title-icon">🎯</span>
            Mission Control
          </h1>
          <p className="mission-subtitle">High-risk space operations with real consequences</p>
          <div className="nasa-status">
            <span className="nasa-badge">🛰️ SPACE DATA LIVE</span>
            <span className="data-count">
              {nasaData.asteroids.length} Asteroids • {nasaData.solarFlares.length} Solar Events
            </span>
          </div>
        </div>
      </div>

      <div className="mission-dashboard">
        <div className="mission-stats">
          <div className="stat-card">
            <span className="stat-icon">🚀</span>
            <div className="stat-info">
              <span className="stat-value">{activeMissions.length}</span>
              <span className="stat-label">Active Missions</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">✅</span>
            <div className="stat-info">
              <span className="stat-value">{missionLog.filter((m) => m.status === "completed").length}</span>
              <span className="stat-label">Successful</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💀</span>
            <div className="stat-info">
              <span className="stat-value">{missionLog.filter((m) => m.status === "failed").length}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">📡</span>
            <div className="stat-info">
              <span className="stat-value">{availableMissions.length}</span>
              <span className="stat-label">Available</span>
            </div>
          </div>
        </div>

        <div className="mission-sections">
          {/* Active Missions */}
          <div className="mission-section">
            <h2 className="section-title">
              <span className="section-icon">⏳</span>
              Active Missions ({activeMissions.length})
            </h2>
            <div className="missions-grid">
              {activeMissions.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">🌌</span>
                  <p>No active missions</p>
                  <p className="empty-subtitle">Launch a mission to begin operations</p>
                </div>
              ) : (
                activeMissions.map((mission) => (
                  <div key={mission.id} className="mission-card active-mission">
                    <div className="mission-header">
                      <span className="mission-icon">{getMissionTypeIcon(mission.type)}</span>
                      <div className="mission-info">
                        <h3 className="mission-name">{mission.title}</h3>
                        <p className="mission-desc">{mission.description}</p>
                      </div>
                    </div>
                    <div className="mission-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${((mission.endTime - mission.startTime - mission.timeLeft) / (mission.endTime - mission.startTime)) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="time-remaining">{formatTime(mission.timeLeft)} remaining</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Available Missions */}
          <div className="mission-section">
            <h2 className="section-title">
              <span className="section-icon">📋</span>
              Available Missions
            </h2>
            <div className="missions-grid">
              {availableMissions.map((mission) => (
                <div key={mission.id} className="mission-card available-mission">
                  <div className="mission-header">
                    <span className="mission-icon">{getMissionTypeIcon(mission.type)}</span>
                    <div className="mission-info">
                      <h3 className="mission-name">{mission.title}</h3>
                      <span
                        className="difficulty-badge"
                        style={{ backgroundColor: getDifficultyColor(mission.difficulty) }}
                      >
                        {mission.difficulty}
                      </span>
                    </div>
                  </div>

                  <p className="mission-desc">{mission.description}</p>
                  <div className="mission-duration">⏱️ Duration: {mission.duration}</div>

                  <div className="mission-requirements">
                    <h4>Requirements:</h4>
                    <div className="requirements-list">
                      {Object.entries(mission.requirements).map(([resource, amount]) => (
                        <span
                          key={resource}
                          className={`requirement ${gameState.resources[resource] >= amount ? "met" : "unmet"}`}
                        >
                          {amount} {resource}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mission-rewards">
                    <h4>Potential Rewards:</h4>
                    <div className="rewards-list">
                      {Object.entries(mission.rewards).map(([resource, amount]) => (
                        <span key={resource} className="reward positive">
                          +{amount} {resource}
                        </span>
                      ))}
                    </div>
                  </div>

                  {mission.risks && (
                    <div className="mission-risks">
                      <h4>⚠️ Failure Risks:</h4>
                      <div className="risks-list">
                        {Object.entries(mission.risks).map(([resource, amount]) => (
                          <span key={resource} className="risk negative">
                            {amount} {resource}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    className={`launch-button ${canStartMission(mission) ? "enabled" : "disabled"}`}
                    onClick={() => startMission(mission)}
                    disabled={!canStartMission(mission)}
                  >
                    <span className="button-icon">🚀</span>
                    Launch Mission
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Mission Log */}
          {missionLog.length > 0 && (
            <div className="mission-section">
              <h2 className="section-title">
                <span className="section-icon">📜</span>
                Mission Log
              </h2>
              <div className="mission-log">
                {missionLog.map((mission, index) => (
                  <div key={`${mission.id}-${index}`} className={`log-entry ${mission.status}`}>
                    <span className="log-icon">{mission.status === "completed" ? "✅" : "💀"}</span>
                    <div className="log-info">
                      <span className="log-mission">{mission.title}</span>
                      <span className="log-time">{new Date(mission.completedAt).toLocaleString()}</span>
                    </div>
                    <div className="log-status">
                      <span className={`status-badge ${mission.status}`}>
                        {mission.status === "completed" ? "SUCCESS" : "FAILED"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MissionControl
